import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup } from '@angular/forms';
import { CalendarModule, MultiSelectModule, SelectItem, SharedModule, TableModule } from 'primeng';
import { LazyLoadEvent } from 'primeng/api';
import moment from 'moment';
import { VmeCommonModule } from '../../../vme-common.module';
import { VmePipesModule } from '../../../vme-pipes.module';
import { SupplierRiskManagementService } from '../../../services/supplier-risk-management.service';
import {
  IAssignmentHistoryDTO,
  IAssignmentHistoryEntry,
} from '../../../interfaces/supplier-risk-management/assignment-history';
import { IAssignmentHistorySearch } from '../../../interfaces/supplier-risk-management/assignment-history-search';
import {
  ASSIGNMENT_ACTION_LABELS,
  RiskManagementAssignmentAction,
} from '../../../enums/supplier-risk-management/risk-management-assignment-action.enum';
import { PillComponent } from '../components/pill/pill.component';

interface IAssignmentHistoryFilterValues {
  supplierIds: number[];
  assigneeUserIds: string[];
  actionedByUserIds: string[];
  actions: RiskManagementAssignmentAction[];
  dateFrom: Date;
  dateTo: Date;
}

/**
 * The audit trail of supplier assignments — who was assigned to which supplier, when, and by whom.
 * The current assignment is deleted on unassignment, so this view is the only record of what
 * happened. Restricted to risk management admins.
 */
@Component({
  selector: 'assignment-history',
  standalone: true,
  imports: [
    CommonModule,
    CalendarModule,
    MultiSelectModule,
    SharedModule,
    TableModule,
    VmeCommonModule,
    VmePipesModule,
    PillComponent,
  ],
  templateUrl: './assignment-history.component.html',
  styleUrls: ['./assignment-history.component.scss'],
})
export class AssignmentHistoryComponent implements OnInit {
  protected data: IAssignmentHistoryDTO;
  protected isLoading: boolean = true;

  protected pageSize: number = 25;
  protected pageIndex: number = 0;

  protected filterForm: FormGroup;

  protected suppliers: SelectItem[] = [];
  protected users: SelectItem[] = [];

  protected readonly actions: SelectItem[] = [
    { value: RiskManagementAssignmentAction.Assigned, label: 'Assigned' },
    { value: RiskManagementAssignmentAction.Reassigned, label: 'Reassigned' },
    { value: RiskManagementAssignmentAction.Unassigned, label: 'Unassigned' },
  ];

  constructor(private supplierRiskManagementService: SupplierRiskManagementService) {
    this.initFilters();
  }

  async ngOnInit() {
    // The initial page of data is loaded by the table's first lazy load event.
    const options = await this.supplierRiskManagementService.getAssignmentHistoryFilterOptions();
    this.suppliers = options.suppliers;
    this.users = options.users;
  }

  private initFilters() {
    this.filterForm = new FormGroup({
      supplierIds: new FormControl<number[]>(null),
      assigneeUserIds: new FormControl<string[]>(null),
      actionedByUserIds: new FormControl<string[]>(null),
      actions: new FormControl<RiskManagementAssignmentAction[]>(null),
      dateFrom: new FormControl<Date>(null),
      dateTo: new FormControl<Date>(null),
    });
  }

  protected async search() {
    this.pageIndex = 0;
    await this.loadData();
  }

  protected async resetFilters() {
    this.initFilters();
    await this.search();
  }

  /** p-table is lazy so that paging happens in the database rather than in the browser. */
  protected async loadLazy(event: LazyLoadEvent) {
    this.pageSize = event.rows;
    this.pageIndex = event.rows > 0 ? Math.floor(event.first / event.rows) : 0;
    await this.loadData();
  }

  protected async loadData() {
    this.isLoading = true;

    const values = this.filterForm.value as IAssignmentHistoryFilterValues;

    const request: IAssignmentHistorySearch = {
      filters: {
        supplierIds: { value: values.supplierIds ?? [] },
        assigneeUserIds: { value: values.assigneeUserIds ?? [] },
        actionedByUserIds: { value: values.actionedByUserIds ?? [] },
        actions: { value: values.actions ?? [] },
        // Sent as a plain date so that the day the user picked is the day the server filters on.
        dateFrom: { value: this.toDateOnly(values.dateFrom) },
        dateTo: { value: this.toDateOnly(values.dateTo) },
      },
      first: this.pageIndex * this.pageSize,
      rows: this.pageSize,
    };

    try {
      this.data = await this.supplierRiskManagementService.getAssignmentHistory(request);
    } finally {
      this.isLoading = false;
    }
  }

  private toDateOnly(date: Date): string {
    return date == null ? null : moment(date).format('YYYY-MM-DD');
  }

  protected actionLabel(entry: IAssignmentHistoryEntry): string {
    return ASSIGNMENT_ACTION_LABELS[entry.action] ?? 'Unknown';
  }

  /** Unassignments are the ones that lose information, so they are called out in the list. */
  protected isUnassignment(entry: IAssignmentHistoryEntry): boolean {
    return entry.action === RiskManagementAssignmentAction.Unassigned;
  }
}
